import { useEffect, useMemo, useRef, useState, type ReactNode } from 'react'
import { createPortal } from 'react-dom'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import {
  FiBell, FiCheck, FiTrash2, FiEye, FiEyeOff, FiMoreVertical,
  FiInbox, FiAlertTriangle, FiAlertCircle, FiInfo, FiCheckCircle, FiExternalLink,
} from 'react-icons/fi'
import { useNavigate } from 'react-router-dom'
import { apiClient } from '../../api/client'
import { API_ENDPOINTS } from '../../api/endpoints'
import { useLanguageStore } from '../../store/languageStore'
import { useNotificationStore } from '../../store/notificationStore'
import { useFuelNotificationStore } from '../../store/fuelNotificationStore'
import { useInboxNotificationStore } from '../../store/inboxNotificationStore'
import { compactQty, compactRelativeTime, parseStockLine, sourceLabel } from '../../utils/notificationPresentation'
import { getRemoteHeartbeatIntervalMs, withHeartbeatJitter } from '../../services/apiConfig'

type TabKey = 'all' | 'unread' | 'hidden'
type Source = 'computed' | 'inbox' | 'fuel' | 'local'

const STATE_LS = 'sa-notification-user-state'

function readLocalStates(): UserState[] {
  try {
    const rows = JSON.parse(localStorage.getItem(STATE_LS) || '[]')
    return Array.isArray(rows) ? rows : []
  } catch {
    return []
  }
}

function writeLocalStates(rows: UserState[]) {
  localStorage.setItem(STATE_LS, JSON.stringify(rows))
}

interface UserState {
  source: string
  notificationKey: string
  isRead: boolean
  isHidden: boolean
  isDeleted: boolean
}

interface CenterItem {
  uid: string
  source: Source
  key: string
  title: string
  message: string
  summary: string
  kind: 'info' | 'success' | 'warning' | 'error'
  date?: string
  actionUrl?: string
  isRead: boolean
  isHidden: boolean
  details: { label: string; value: string }[]
}

type Props = { externalNotifications?: unknown[] }

export default function NotificationCenter(_props: Props) {
  const language = useLanguageStore((s) => s.language)
  const isRTL = language === 'ar'
  const navigate = useNavigate()
  const qc = useQueryClient()
  const panelRef = useRef<HTMLDivElement>(null)
  const [open, setOpen] = useState(false)
  const [tab, setTab] = useState<TabKey>('all')
  const [menuFor, setMenuFor] = useState<string | null>(null)
  const [menuPos, setMenuPos] = useState<{ top: number; left: number } | null>(null)
  const [headerMenu, setHeaderMenu] = useState(false)
  const [details, setDetails] = useState<CenterItem | null>(null)

  const local = useNotificationStore()
  const fuelItems = useFuelNotificationStore((s) => s.items)
  const inboxItems = useInboxNotificationStore((s) => s.items)

  const notifInterval = useMemo(
    () => withHeartbeatJitter(getRemoteHeartbeatIntervalMs(60_000)),
    [],
  )

  const alertsQ = useQuery({
    queryKey: ['allNotifications'],
    queryFn: async () => {
      const response = await apiClient.get(API_ENDPOINTS.notifications.all)
      return response.data
    },
    refetchInterval: notifInterval,
    refetchIntervalInBackground: false,
    staleTime: notifInterval,
    retry: 0,
  })

  const stateQ = useQuery({
    queryKey: ['notification-user-state'],
    queryFn: async () => {
      const res = await apiClient.get<UserState[]>(API_ENDPOINTS.notifications.state)
      const server = Array.isArray(res.data) ? res.data : []
      const localRows = readLocalStates()
      const map = new Map<string, UserState>()
      for (const row of [...localRows, ...server]) {
        const source = String((row as any).source ?? (row as any).Source ?? '').toLowerCase()
        const notificationKey = String((row as any).notificationKey ?? (row as any).NotificationKey ?? '')
        map.set(`${source}|${notificationKey}`, {
          source,
          notificationKey,
          isRead: !!(row.isRead ?? (row as any).IsRead),
          isHidden: !!(row.isHidden ?? (row as any).IsHidden),
          isDeleted: !!(row.isDeleted ?? (row as any).IsDeleted),
        })
      }
      const merged = [...map.values()]
      writeLocalStates(merged)
      return merged
    },
    initialData: readLocalStates(),
    staleTime: notifInterval,
    retry: 0,
    refetchOnWindowFocus: false,
  })

  useEffect(() => {
    if (!open) return
    const onDown = (e: MouseEvent) => {
      const t = e.target as Node
      if (panelRef.current && !panelRef.current.contains(t)) {
        const el = t as HTMLElement
        if (el.closest?.('[data-notif-menu]')) return
        setOpen(false)
        setMenuFor(null)
        setHeaderMenu(false)
      }
    }
    document.addEventListener('mousedown', onDown)
    return () => document.removeEventListener('mousedown', onDown)
  }, [open])

  const stateMap = useMemo(() => {
    const map = new Map<string, UserState>()
    for (const row of stateQ.data || []) {
      const source = String((row as any).source ?? (row as any).Source ?? '').toLowerCase()
      const notificationKey = String((row as any).notificationKey ?? (row as any).NotificationKey ?? '')
      map.set(`${source}|${notificationKey}`, {
        source,
        notificationKey,
        isRead: !!(row.isRead ?? (row as any).IsRead),
        isHidden: !!(row.isHidden ?? (row as any).IsHidden),
        isDeleted: !!(row.isDeleted ?? (row as any).IsDeleted),
      })
    }
    return map
  }, [stateQ.data])

  const items = useMemo(() => {
    const out: CenterItem[] = []

    const overlay = (source: Source, key: string, fallbackRead: boolean, fallbackHidden: boolean) => {
      const st = stateMap.get(`${source}|${key}`)
      return {
        isRead: st?.isRead || fallbackRead,
        isHidden: st?.isHidden || fallbackHidden,
        isDeleted: !!st?.isDeleted,
      }
    }

    for (const n of alertsQ.data?.notifications || []) {
      const type = String(n.type || 'info')
      const id = String(n.id ?? n.itemId ?? n.title ?? '')
      const key = `${type}:${id}`
      const flags = overlay('computed', key, false, false)
      if (flags.isDeleted) continue
      const title = String(n.title || '')
      const message = String(n.message || '')
      const stock = parseStockLine(message)
      const qty = n.quantity != null ? compactQty(n.quantity) : stock.qty
      const min = n.miniQty != null ? compactQty(n.miniQty) : stock.min
      const summary = qty || min
        ? (isRTL
          ? `الكمية الحالية ${qty || '—'} · الحد الأدنى ${min || '—'}`
          : `Current ${qty || '—'} · Min ${min || '—'}`)
        : compactLine(message)
      out.push({
        uid: `computed-${key}`,
        source: 'computed',
        key,
        title,
        message,
        summary,
        kind: mapKind(type),
        actionUrl: n.actionUrl,
        isRead: flags.isRead,
        isHidden: flags.isHidden,
        details: [
          qty ? { label: isRTL ? 'الكمية الحالية' : 'Current qty', value: qty } : null,
          min ? { label: isRTL ? 'الحد الأدنى' : 'Minimum', value: min } : null,
          n.storeName ? { label: isRTL ? 'المخزن / الخزان' : 'Warehouse', value: String(n.storeName) } : null,
          { label: isRTL ? 'مصدر الإشعار' : 'Source', value: sourceLabel('computed', type, isRTL) },
        ].filter(Boolean) as { label: string; value: string }[],
      })
    }

    for (const n of inboxItems) {
      const flags = overlay('inbox', n.id, n.isRead, !!n.isHidden || !!n.isArchived)
      if (flags.isDeleted) continue
      out.push({
        uid: `inbox-${n.id}`,
        source: 'inbox',
        key: n.id,
        title: n.title,
        message: n.message,
        summary: compactLine(n.message),
        kind: n.priority === 'critical' ? 'error' : n.priority === 'high' ? 'warning' : 'info',
        date: n.createdAt,
        actionUrl: n.actionUrl,
        isRead: flags.isRead,
        isHidden: flags.isHidden,
        details: [
          n.entityName ? { label: isRTL ? 'المستند' : 'Entity', value: `${n.entityName} ${n.entityId || ''}`.trim() } : null,
          n.createdAt ? { label: isRTL ? 'تاريخ الإشعار' : 'Date', value: new Date(n.createdAt).toLocaleString() } : null,
          { label: isRTL ? 'مصدر الإشعار' : 'Source', value: sourceLabel('inbox', '', isRTL) },
        ].filter(Boolean) as { label: string; value: string }[],
      })
    }

    for (const n of fuelItems) {
      const flags = overlay('fuel', n.notificationId, n.isRead, !!n.isHidden)
      if (flags.isDeleted) continue
      const stock = parseStockLine(n.message)
      out.push({
        uid: `fuel-${n.notificationId}`,
        source: 'fuel',
        key: n.notificationId,
        title: n.title,
        message: n.message,
        summary: stock.qty || stock.min
          ? (isRTL
            ? `الكمية الحالية ${stock.qty || '—'} · الحد الأدنى ${minOr(stock.min)}`
            : `Current ${stock.qty || '—'} · Min ${minOr(stock.min)}`)
          : compactLine(n.message),
        kind: n.priority === 'critical' ? 'error' : n.needsAction ? 'warning' : 'info',
        date: n.createdAt,
        actionUrl: n.actionUrl || undefined,
        isRead: flags.isRead,
        isHidden: flags.isHidden,
        details: [
          stock.qty ? { label: isRTL ? 'الكمية الحالية' : 'Current qty', value: stock.qty } : null,
          stock.min ? { label: isRTL ? 'الحد الأدنى' : 'Minimum', value: stock.min } : null,
          n.stationName ? { label: isRTL ? 'المحطة' : 'Station', value: n.stationName } : null,
          n.employeeName ? { label: isRTL ? 'الموظف' : 'Employee', value: n.employeeName } : null,
          n.createdAt ? { label: isRTL ? 'تاريخ الإشعار' : 'Date', value: new Date(n.createdAt).toLocaleString() } : null,
          { label: isRTL ? 'مصدر الإشعار' : 'Source', value: sourceLabel('fuel', '', isRTL) },
        ].filter(Boolean) as { label: string; value: string }[],
      })
    }

    for (const n of local.notifications) {
      const flags = overlay('local', n.id, n.read, !!n.hidden)
      if (flags.isDeleted) continue
      out.push({
        uid: `local-${n.id}`,
        source: 'local',
        key: n.id,
        title: n.title,
        message: n.message,
        summary: compactLine(n.message),
        kind: n.type,
        date: n.date,
        actionUrl: n.actionLink,
        isRead: flags.isRead,
        isHidden: flags.isHidden,
        details: [
          n.date ? { label: isRTL ? 'تاريخ الإشعار' : 'Date', value: new Date(n.date).toLocaleString() } : null,
          { label: isRTL ? 'مصدر الإشعار' : 'Source', value: sourceLabel('local', '', isRTL) },
        ].filter(Boolean) as { label: string; value: string }[],
      })
    }

    const seen = new Set<string>()
    return out.filter((x) => {
      if (seen.has(x.uid)) return false
      seen.add(x.uid)
      return true
    }).sort((a, b) => {
      const da = a.date ? new Date(a.date).getTime() : 0
      const db = b.date ? new Date(b.date).getTime() : 0
      return db - da
    })
  }, [alertsQ.data, inboxItems, fuelItems, local.notifications, stateMap, isRTL])

  const visible = items.filter((n) => {
    if (tab === 'hidden') return n.isHidden
    if (n.isHidden) return false
    if (tab === 'unread') return !n.isRead
    return true
  })

  const unreadCount = items.filter((n) => !n.isRead && !n.isHidden).length

    const apply = async (item: CenterItem, action: 'read' | 'hide' | 'unhide' | 'delete') => {
    qc.setQueryData<UserState[]>(['notification-user-state'], (old = []) => {
      const match = (x: UserState) =>
        `${(x.source || '').toLowerCase()}|${x.notificationKey || (x as any).NotificationKey}` === `${item.source}|${item.key}`
      const prev = old.find(match)
      const row: UserState = {
        source: item.source,
        notificationKey: item.key,
        isRead: action === 'unhide' ? !!prev?.isRead || item.isRead : true,
        isHidden: action === 'hide' ? true : action === 'unhide' ? false : !!prev?.isHidden,
        isDeleted: action === 'delete',
      }
      const next = [...old.filter((x) => !match(x)), row]
      writeLocalStates(next)
      return next
    })
    if (item.source === 'local') {
      if (action === 'read') local.markAsRead(item.key)
      if (action === 'hide') local.hideNotification(item.key)
      if (action === 'unhide') local.unhideNotification(item.key)
      if (action === 'delete') local.removeNotification(item.key)
    }
    if (item.source === 'inbox') {
      const store = useInboxNotificationStore.getState()
      if (action === 'read') store.markLocalRead(item.key)
      if (action === 'hide') store.hideLocal(item.key)
      if (action === 'unhide') store.unhideLocal(item.key)
      if (action === 'delete') store.deleteLocal(item.key)
    }
    if (item.source === 'fuel') {
      const store = useFuelNotificationStore.getState()
      if (action === 'read') store.markLocalRead(item.key)
      if (action === 'hide') store.hideLocal(item.key)
      if (action === 'unhide') store.unhideLocal(item.key)
      if (action === 'delete') store.deleteLocal(item.key)
    }
    setMenuFor(null)
    if (action === 'delete' || action === 'hide') setDetails((d) => (d?.uid === item.uid ? null : d))
    try {
      await apiClient.post(API_ENDPOINTS.notifications.state, { source: item.source, key: item.key, action })
      void qc.invalidateQueries({ queryKey: ['notification-user-state'] })
    } catch {
      /* optimistic row stays until the API is available */
    }
  }

  const markAllRead = async () => {
    const pending = items.filter((n) => !n.isRead && !n.isHidden)
    qc.setQueryData<UserState[]>(['notification-user-state'], (old = []) => {
      const next = [...old]
      for (const n of pending) {
        const idx = next.findIndex((x) => `${(x.source || '').toLowerCase()}|${x.notificationKey}` === `${n.source}|${n.key}`)
        const row: UserState = {
          source: n.source,
          notificationKey: n.key,
          isRead: true,
          isHidden: false,
          isDeleted: false,
        }
        if (idx >= 0) next[idx] = { ...next[idx], ...row, isHidden: next[idx].isHidden }
        else next.push(row)
      }
      writeLocalStates(next)
      return next
    })
    local.markAllAsRead()
    useInboxNotificationStore.getState().markAllLocalRead()
    useFuelNotificationStore.getState().markAllLocalRead()
    try {
      await apiClient.post(API_ENDPOINTS.notifications.stateReadAll, {
        items: pending.map((n) => ({ source: n.source, key: n.key })),
      })
      void qc.invalidateQueries({ queryKey: ['notification-user-state'] })
    } catch {
      /* optimistic */
    }
    setHeaderMenu(false)
  }

  const openKebab = (uid: string, el: HTMLElement) => {
    const r = el.getBoundingClientRect()
    setMenuPos({ top: r.bottom + 4, left: isRTL ? r.left : r.right - 196 })
    setMenuFor((cur) => (cur === uid ? null : uid))
    setHeaderMenu(false)
  }

  const icon = (kind: CenterItem['kind']) => {
    if (kind === 'success') return <FiCheckCircle className="h-4 w-4 text-emerald-500" />
    if (kind === 'warning') return <FiAlertTriangle className="h-4 w-4 text-amber-500" />
    if (kind === 'error') return <FiAlertCircle className="h-4 w-4 text-rose-500" />
    return <FiInfo className="h-4 w-4 text-sky-500" />
  }

  const menuItem = items.find((n) => n.uid === menuFor)

  return (
    <div className="relative inline-block text-left" ref={panelRef}>
      <button
        type="button"
        onClick={() => { setOpen((v) => !v); setMenuFor(null); setHeaderMenu(false) }}
        className="relative p-2.5 text-gray-400 hover:text-white focus:outline-none rounded-2xl transition-all duration-300 hover:bg-white/10 group"
      >
        <FiBell className="h-6 w-6" />
        {unreadCount > 0 && (
          <span className="absolute top-1.5 right-1.5 h-5 w-5 rounded-full ring-2 ring-gray-900 bg-accent text-white text-[10px] font-black flex items-center justify-center">
            {unreadCount > 9 ? '9+' : unreadCount}
          </span>
        )}
      </button>

      {open && (
        <div
          className={`absolute ${isRTL ? 'left-0' : 'right-0'} mt-3 w-[22rem] sm:w-[26rem] rounded-2xl bg-white shadow-xl ring-1 ring-black/5 z-50 overflow-hidden`}
          dir={isRTL ? 'rtl' : 'ltr'}
        >
          <div className="px-4 pt-4 pb-2">
            <div className={`flex items-start justify-between gap-2 ${isRTL ? 'flex-row-reverse' : ''}`}>
              <div className={isRTL ? 'text-right' : 'text-left'}>
                <h3 className="text-sm font-semibold text-gray-900">{isRTL ? 'مركز الإشعارات' : 'Notification center'}</h3>
                <p className="text-xs text-gray-500 mt-0.5">
                  {unreadCount} {isRTL ? 'غير مقروءة' : 'unread'}
                </p>
              </div>
              <div className={`flex items-center gap-1 ${isRTL ? 'flex-row-reverse' : ''}`}>
                <button
                  type="button"
                  onClick={() => void markAllRead()}
                  className="p-1.5 rounded-lg text-gray-500 hover:bg-gray-100"
                  title={isRTL ? 'تحديد الكل كمقروء' : 'Mark all as read'}
                >
                  <FiCheck className="h-4 w-4" />
                </button>
                <button
                  type="button"
                  onClick={() => { setHeaderMenu((v) => !v); setMenuFor(null) }}
                  className="p-1.5 rounded-lg text-gray-500 hover:bg-gray-100"
                  title={isRTL ? 'خيارات' : 'Options'}
                >
                  <FiMoreVertical className="h-4 w-4" />
                </button>
              </div>
            </div>
            {headerMenu && (
              <div className="mt-2 rounded-lg border border-gray-100 bg-white shadow-sm py-1 text-sm">
                <button type="button" className="w-full px-3 py-2 text-start hover:bg-gray-50" onClick={() => void markAllRead()}>
                  {isRTL ? 'تحديد الكل كمقروء' : 'Mark all as read'}
                </button>
                <button
                  type="button"
                  className="w-full px-3 py-2 text-start text-rose-600 hover:bg-rose-50"
                  onClick={() => {
                    visible.forEach((n) => void apply(n, 'delete'))
                    setHeaderMenu(false)
                  }}
                >
                  {isRTL ? 'حذف الظاهرة' : 'Delete visible'}
                </button>
              </div>
            )}
            <div className={`mt-3 flex gap-1 ${isRTL ? 'flex-row-reverse' : ''}`}>
              {([
                ['all', isRTL ? 'الكل' : 'All'],
                ['unread', isRTL ? 'غير مقروءة' : 'Unread'],
                ['hidden', isRTL ? 'مخفية' : 'Hidden'],
              ] as const).map(([id, label]) => (
                <button
                  key={id}
                  type="button"
                  onClick={() => setTab(id)}
                  className={`px-2.5 py-1 rounded-full text-xs ${tab === id ? 'bg-teal-600 text-white' : 'text-gray-600 hover:bg-gray-100'}`}
                >
                  {label}
                </button>
              ))}
            </div>
          </div>

          <div className="max-h-[22rem] overflow-y-auto">
            {visible.length === 0 ? (
              <div className="px-4 py-10 text-center">
                <FiInbox className="h-8 w-8 text-gray-300 mx-auto mb-2" />
                <p className="text-sm text-gray-400">{isRTL ? 'لا توجد إشعارات' : 'No notifications'}</p>
              </div>
            ) : (
              visible.map((n) => (
                <div
                  key={n.uid}
                  className={`px-4 py-2.5 border-t border-gray-100 first:border-t-0 ${!n.isRead ? 'bg-teal-50/40' : ''}`}
                >
                  <div className={`flex gap-2.5 ${isRTL ? 'flex-row-reverse' : ''}`}>
                    <div className="mt-0.5 shrink-0">{icon(n.kind)}</div>
                    <button
                      type="button"
                      className={`flex-1 min-w-0 ${isRTL ? 'text-right' : 'text-left'}`}
                      onClick={() => {
                        if (!n.isRead) void apply(n, 'read')
                        setDetails(n)
                      }}
                    >
                      <div className={`text-[13px] leading-snug ${n.isRead ? 'font-medium text-gray-800' : 'font-semibold text-gray-900'}`}>
                        {n.title}
                      </div>
                      {n.summary && (
                        <div className="text-[11px] text-gray-500 mt-0.5 truncate">{n.summary}</div>
                      )}
                      {n.date && (
                        <div className="text-[10px] text-gray-400 mt-0.5">{compactRelativeTime(n.date, isRTL)}</div>
                      )}
                    </button>
                    <button
                      type="button"
                      className="p-1 h-fit rounded-md text-gray-400 hover:bg-gray-100 shrink-0"
                      onClick={(e) => openKebab(n.uid, e.currentTarget)}
                    >
                      <FiMoreVertical className="h-4 w-4" />
                    </button>
                  </div>
                </div>
              ))
            )}
          </div>

          <div className="px-3 py-2 border-t border-gray-100">
            <button
              type="button"
              onClick={() => { setOpen(false); navigate('/notifications/inbox') }}
              className="w-full py-2 text-xs text-gray-600 hover:bg-gray-50 rounded-lg"
            >
              {isRTL ? 'عرض كافة التنبيهات' : 'View all'}
            </button>
          </div>
        </div>
      )}

      {menuFor && menuItem && menuPos && createPortal(
        <div
          data-notif-menu
          className="fixed z-[80] w-48 rounded-lg border border-gray-100 bg-white shadow-lg py-1 text-sm"
          style={{ top: menuPos.top, left: Math.max(8, menuPos.left) }}
          dir={isRTL ? 'rtl' : 'ltr'}
        >
          <MenuBtn icon={<FiEye />} onClick={() => { setDetails(menuItem); setMenuFor(null) }}>{isRTL ? 'عرض التفاصيل' : 'View details'}</MenuBtn>
          {!menuItem.isRead && (
            <MenuBtn icon={<FiCheck />} onClick={() => void apply(menuItem, 'read')}>{isRTL ? 'تحديد كمقروء' : 'Mark as read'}</MenuBtn>
          )}
          {menuItem.isHidden ? (
            <MenuBtn icon={<FiEye />} onClick={() => void apply(menuItem, 'unhide')}>{isRTL ? 'إظهار الإشعار' : 'Unhide'}</MenuBtn>
          ) : (
            <MenuBtn icon={<FiEyeOff />} onClick={() => void apply(menuItem, 'hide')}>{isRTL ? 'إخفاء الإشعار' : 'Hide'}</MenuBtn>
          )}
          <MenuBtn icon={<FiTrash2 />} danger onClick={() => void apply(menuItem, 'delete')}>{isRTL ? 'حذف الإشعار' : 'Delete'}</MenuBtn>
        </div>,
        document.body
      )}

      {details && createPortal(
        <div className="fixed inset-0 z-[90] bg-black/40 flex items-end sm:items-center justify-center p-4" onClick={() => setDetails(null)}>
          <div
            className="w-full max-w-md bg-white rounded-2xl shadow-xl p-5"
            dir={isRTL ? 'rtl' : 'ltr'}
            onClick={(e) => e.stopPropagation()}
          >
            <div className={`flex items-start gap-3 ${isRTL ? 'flex-row-reverse' : ''}`}>
              <div className="mt-0.5">{icon(details.kind)}</div>
              <div className={`flex-1 ${isRTL ? 'text-right' : 'text-left'}`}>
                <h3 className="text-base font-semibold text-gray-900">{details.title}</h3>
                <p className="text-sm text-gray-600 mt-2 whitespace-pre-line">{details.message}</p>
                <dl className="mt-4 space-y-2">
                  {details.details.map((row) => (
                    <div key={row.label} className={`flex justify-between gap-3 text-sm ${isRTL ? 'flex-row-reverse' : ''}`}>
                      <dt className="text-gray-500">{row.label}</dt>
                      <dd className="text-gray-800 font-medium">{row.value}</dd>
                    </div>
                  ))}
                </dl>
                {details.actionUrl && (
                  <button
                    type="button"
                    className="mt-4 btn-primary w-full flex items-center justify-center gap-2"
                    onClick={() => {
                      const url = details.actionUrl!
                      setDetails(null)
                      setOpen(false)
                      navigate(url)
                    }}
                  >
                    <FiExternalLink /> {isRTL ? 'الانتقال إلى العملية' : 'Open related record'}
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>,
        document.body
      )}
    </div>
  )
}

function MenuBtn({ children, icon, onClick, danger }: { children: ReactNode; icon: ReactNode; onClick: () => void; danger?: boolean }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`w-full px-3 py-2 flex items-center gap-2 hover:bg-gray-50 ${danger ? 'text-rose-600' : 'text-gray-700'}`}
    >
      <span className="text-base">{icon}</span>
      {children}
    </button>
  )
}

function compactLine(message: string) {
  const first = (message || '').split('\n')[0].trim()
  return first.length > 90 ? `${first.slice(0, 88)}…` : first
}

function minOr(v?: string) {
  return v || '—'
}

function mapKind(type: string): CenterItem['kind'] {
  switch (type.toLowerCase()) {
    case 'success': return 'success'
    case 'warning':
    case 'lowstock':
    case 'itemexpiry':
      return 'warning'
    case 'error':
    case 'customerinstallment':
    case 'vendorinstallment':
      return 'error'
    default: return 'info'
  }
}
